import React, { useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import Form from "react-bootstrap/Form";
import default_module_img from "@me-team/host/src/images/default_module_img.png";
import { useTranslation } from "react-i18next";
import styles from "../ModulesListStyles.module.scss";
import { ModulesTagName, SubModulesTagName } from "../../../../../utils/enums";
import { useParams } from "react-router-dom";
import { DisableModuleDataInterface } from "../../../../../interfaces/interfaces";
import ModulesService from "../../../../../services/ModulesService/ModulesService";

type ClientsModuleProps = {
    modulesList: ModuleProxy[];
    updateModules: (
        companyId: number,
        updates: ModuleInputType[],
        branchId: number,
        isNeededToDisable?: boolean
    ) => Promise<void>;
    openDisableModal: (moduleData: DisableModuleDataInterface) => void;
    canManageModules: boolean;
};

const ClientsModule: React.FC<ClientsModuleProps> = (props) => {
    const { currentCompanyId, branchId } = useParams();
    const { t: translate } = useTranslation();
    const CLIENTS_MODULE: ModuleProxy = props.modulesList.find(
        (module: ModuleProxy) => module.name === ModulesTagName.CLIENTS
    );
    const basePath: string = process.env.REACT_APP_POS_URL;
    const imageSRC: string = CLIENTS_MODULE.image
        ? `${basePath}/build/images/modules/${CLIENTS_MODULE.image}`
        : default_module_img;
    const LOGBOOK_MODULE: ModuleProxy = props.modulesList.find(
        (module: ModuleProxy) => module.name === ModulesTagName.LOGBOOK
    );
    const [isMainSwitchDisable, setIsMainSwitchDisable] = useState(false);
    const [isChildModuleDisabled, setIsChildModuleDisabled] = useState(false);
    const isVisitsHistoryModuleDisabled: (module: ModuleProxy) => boolean = (module: ModuleProxy): boolean =>
        module.name !== SubModulesTagName.VISIT_HISTORY || !LOGBOOK_MODULE.active || isChildModuleDisabled;

    const onToggleClientsModule = async () => {
        if (!props.canManageModules) return;

        setIsMainSwitchDisable(true);

        const updates: ModuleInputType[] = [{ id: CLIENTS_MODULE.id, isActive: !CLIENTS_MODULE.active }];

        if (CLIENTS_MODULE.active) {
            const disabledData: DisableModuleDataInterface = ModulesService.disabledModule(
                CLIENTS_MODULE,
                props.modulesList
            );

            if (disabledData.dependentModules.length !== 0) {
                props.openDisableModal(disabledData);
                setIsMainSwitchDisable(false);
                return;
            }
        }

        await props.updateModules(Number(currentCompanyId), updates, Number(branchId), true);
        setIsMainSwitchDisable(false);
    };

    const onToggleHistoryVisitModule = async (childModule: ModuleProxy) => {
        if (!props.canManageModules) return;

        setIsChildModuleDisabled(true);
        await props.updateModules(
            Number(currentCompanyId),
            [{ id: childModule.id, isActive: !childModule.active }],
            Number(branchId)
        );
        setIsChildModuleDisabled(false);
    };

    return (
        <div className={"border rounded p-4 mt-4"}>
            <Row>
                <Col xl={2} xxl={2} className="mb-3 mb-xl-0">
                    <div className={!CLIENTS_MODULE.active ? "opacity-75" : ""}>
                        <img
                            className={`${styles.customImagesSize} d-block`}
                            src={imageSRC}
                            alt={CLIENTS_MODULE.name}
                        />
                    </div>
                </Col>
                <Col xl={5} xxl={6} className="mb-3 mb-xl-0">
                    <div className={!CLIENTS_MODULE.active ? "opacity-75" : ""}>
                        <h5 className={"fw-normal mb-2 fs-24"}>{translate(CLIENTS_MODULE.name)}</h5>
                        <p className={"mb-0 text-grey"}>{CLIENTS_MODULE.description}</p>
                        {CLIENTS_MODULE.descriptionInfo && (
                            <p className={"mb-0 text-grey mt-2"}>
                                <i className="bi bi-info-circle me-2"></i>
                                {CLIENTS_MODULE.descriptionInfo}
                            </p>
                        )}
                    </div>
                </Col>
                <Col
                    xs={12}
                    sm={6}
                    md={6}
                    xl={2}
                    xxl={2}
                    className={"d-flex justify-content-start justify-content-sm-center d-xl-block mb-3 mb-sm-0"}
                >
                    <Form className={!CLIENTS_MODULE.active ? "opacity-75" : ""}>
                        <ul className="list-unstyled">
                            {CLIENTS_MODULE.childModules.map((childModule: ModuleProxy, index: number) => (
                                <li
                                    className={`d-flex align-items-center ${
                                        index === CLIENTS_MODULE.childModules.length - 1 ? "" : "mb-3"
                                    }`}
                                    key={childModule.id}
                                >
                                    <Form.Check
                                        type="switch"
                                        label={translate(childModule.name)}
                                        checked={childModule.active}
                                        disabled={!props.canManageModules || isVisitsHistoryModuleDisabled(childModule)}
                                        className="mb-0 custom-module-switch"
                                        onChange={() => onToggleHistoryVisitModule(childModule)}
                                    />
                                    {childModule.name === SubModulesTagName.VISIT_HISTORY && !LOGBOOK_MODULE.active && (
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip id={`tooltip-${childModule.id}`}>
                                                    {translate(
                                                        'Will become available after connecting the \"Record Journal\" module.'
                                                    )}
                                                </Tooltip>
                                            }
                                        >
                                            <i className="bi bi-question-circle ms-2 text-grey cursor-pointer"></i>
                                        </OverlayTrigger>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </Form>
                </Col>
                <Col
                    xs={12}
                    sm={6}
                    md={6}
                    xl={3}
                    xxl={2}
                    className={"d-flex flex-column justify-content-between align-items-center align-items-xl-end"}
                >
                    <Form className={"d-flex align-items-center"}>
                        <Form.Check
                            type="switch"
                            label={CLIENTS_MODULE.active ? translate("Enabled") : translate("Disabled")}
                            checked={CLIENTS_MODULE.active}
                            disabled={isMainSwitchDisable || !props.canManageModules}
                            className="mb-0 custom-module-switch fs-6"
                            onChange={onToggleClientsModule}
                        />
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip id={`tooltip-${CLIENTS_MODULE.id}`}>
                                    {translate(
                                        "You can disable/enable the module functionality at any time without canceling your subscription."
                                    )}
                                </Tooltip>
                            }
                        >
                            <i className="bi bi-question-circle ms-2 text-grey cursor-pointer"></i>
                        </OverlayTrigger>
                    </Form>
                    <h5 className={"mt-3 mt-sm-0"}>{translate("Free")}</h5>
                </Col>
            </Row>
        </div>
    );
};

export default ClientsModule;
